import React from 'react';
import { Link } from 'react-router-dom';
import { Building2, Home, Shield, Rocket, TrendingUp, Headphones, MapPin, Check, ArrowRight } from 'lucide-react';
import PageLayout from '../components/PageLayout';
import UseCasesHero from '../components/UseCasesHero';
import useInView from '../hooks/useInView';

const USE_CASES = [
  {
    icon: Building2,
    accent: 'accent-1',
    label: 'BPO / KPO',
    headline: 'Cut infrastructure costs by 60%',
    body: 'Run a full-scale outbound BPO without EPABX, servers or desk phones. Deploy new agents in hours rather than weeks. Scale up for project peaks and scale down when they end — pay only for what you use.',
    bullets: ['No hardware procurement', 'Agent onboarding in under an hour', 'Multi-campaign management', 'Real-time supervisor view', 'Per-campaign reporting'],
  },
  {
    icon: Home,
    accent: 'accent-3',
    label: 'Real estate',
    headline: 'Follow up faster, close more deals',
    body: 'Your sales team is always on the move. WASLX lets field agents call, log notes and set reminders from their phone without ever opening a laptop. The auto dialer burns through lead lists while you are showing a property.',
    bullets: ['Field-agent friendly', 'Auto dialer for lead lists', 'Instant follow-up reminders', 'Call recordings for compliance', 'Lead disposition tracking'],
  },
  {
    icon: Shield,
    accent: 'accent-4',
    label: 'Insurance / loans',
    headline: 'Compliant calls, complete records',
    body: 'Every call recorded. Every lead tracked. Every agent monitored. A full audit trail for regulatory requirements, without the complexity of enterprise telecom systems.',
    bullets: ['100% call recording', 'Lead disposition tracking', 'Agent performance scoring', 'Campaign-level reporting', 'Supervisor monitoring tools'],
  },
  {
    icon: Rocket,
    accent: 'accent-5',
    label: 'Startups',
    headline: 'Launch call ops today, not next quarter',
    body: 'Skip the procurement headache. No vendor negotiations, no hardware delivery, no IT setup. WASLX gives you serious calling infrastructure from day one, at startup-friendly pricing.',
    bullets: ['Zero upfront cost', 'No contracts required', 'Start with 1 agent, scale to 1000', '30-minute onboarding', 'Cancel or pause anytime'],
  },
  {
    icon: TrendingUp,
    accent: 'accent-6',
    label: 'Sales teams',
    headline: 'More talk time. More pipeline.',
    body: 'The auto dialer eliminates manual dialing and skip rates, so agents spend far more of the day talking to prospects. CRM sync means no double entry, and real-time coaching means faster ramp.',
    bullets: ['Maximised agent talk time', 'Auto lead rotation', 'Live supervisor whisper', 'Pipeline analytics', 'Per-agent scorecards'],
  },
  {
    icon: MapPin,
    accent: 'accent-7',
    label: 'Banking / field sales',
    headline: 'Track every field agent. Close every lead.',
    body: 'When your call center books a KYC appointment or a card visit, WASLX assigns it to the nearest available field agent — or your team lead allocates it by hand. Every interaction is logged, documents are scanned and submitted on site, and the whole field force appears as live dots on the map.',
    bullets: [
      'Auto-assign leads to the nearest field agent by GPS',
      'Team-lead allocation dashboard',
      'Live location tracking on a real-time map',
      'Timestamped interaction logs at every touchpoint',
      'On-site document scan and portal submission',
      'Full audit trail from call center to field closure',
    ],
  },
  {
    icon: Headphones,
    accent: 'accent-8',
    label: 'Customer support',
    headline: 'Remote-ready support operations',
    body: 'Support agents handle outbound follow-ups, callbacks and proactive outreach from anywhere. Live monitoring keeps quality high even when the team is spread across cities.',
    bullets: ['Remote-first by design', 'Quality monitoring tools', 'Callback scheduling', 'Customer history on call', 'Disposition-based routing'],
  },
];

/**
 * One industry, one section.
 *
 * Same rebuild the /features sections got, for the same reason: this was
 * copy on one side and a vector illustration inside a .card--tint on the
 * other, seven times down the page. The drawings restated the heading and
 * each sat in a box of its own, so the page read as seven identical
 * cards rather than seven industries.
 *
 * The bullets take the column the artwork had. They are the specific,
 * industry-by-industry substance a generic illustration was standing in
 * front of, and they sit on hairlines because the home page keeps cards
 * for things you scan in a grid, not for a five-item list.
 *
 * Colour comes from the oversized ghost glyph — the home feature grid's
 * own device at section scale — so each industry keeps its hue.
 */
const UseCaseSection = ({ useCase, index }) => {
  const [ref, inView] = useInView();
  const Icon = useCase.icon;
  const reversed = index % 2 === 1;

  return (
    <section
      ref={ref}
      className={`section reveal ${inView ? 'is-inview' : ''} ${
        index % 2 === 0 ? 'section--surface' : 'section--muted'
      }`}
    >
      <div className="container-page">
        <div className={`${useCase.accent} relative grid items-center gap-12 lg:grid-cols-2`}>
          <span
            className="pointer-events-none absolute -top-16 right-0 hidden text-accent opacity-[0.06] lg:block"
            aria-hidden="true"
          >
            <Icon className="h-64 w-64" strokeWidth={1} />
          </span>

          <div className={`relative animate-fade-up ${reversed ? 'lg:order-2' : ''}`}>
            <span className="eyebrow">
              <Icon className="h-3.5 w-3.5" />
              {useCase.label}
            </span>
            <h2 className="section-title !text-3xl">{useCase.headline}</h2>
            <p className="mt-4 leading-relaxed text-content-muted">{useCase.body}</p>

            <Link to="/contact" className="btn-pill btn-pill--accent mt-8">
              Book a demo for {useCase.label}
              <ArrowRight className="h-4 w-4" />
            </Link>
          </div>

          <ul
            className={`relative animate-fade-up divide-y divide-line border-t border-line ${
              reversed ? 'lg:order-1' : ''
            }`}
            style={{ animationDelay: '120ms' }}
          >
            {useCase.bullets.map(bullet => (
              <li
                key={bullet}
                className="flex items-start gap-3 py-3.5 text-[0.9375rem] text-content"
              >
                <Check className="mt-0.5 h-4 w-4 flex-shrink-0 text-accent-strong" />
                {bullet}
              </li>
            ))}
          </ul>
        </div>
      </div>
    </section>
  );
};

/**
 * The closing panel, split rather than centred — the argument CTABanner
 * makes on the home page: a centred stack puts the action last in the
 * reading order and leaves the headline over dead width. One dark anchor
 * per page is also already the rule here, and the hero is it, so this
 * closes on the light mint plane the other pages close on.
 */
const ClosingCta = () => {
  const [ref, inView] = useInView();

  return (
    <section ref={ref} className={`section section--surface reveal ${inView ? 'is-inview' : ''}`}>
      <div className="container-page">
        <div className="accent-4 card card--mint reveal-color !p-8 lg:!p-12">
          <div className="grid items-center gap-10 lg:grid-cols-[1.15fr_0.85fr] lg:gap-14">
            <div>
              <h2 className="text-section font-bold leading-[1.08] tracking-tight text-content">
                Your industry <span className="display-accent">not listed?</span>
              </h2>
              <p className="mt-4 max-w-xl text-lede leading-relaxed text-content-muted">
                If your team makes outbound calls on Android, WASLX works for you. Tell us about
                your setup and we will show you how.
              </p>
            </div>

            <Link to="/contact" className="btn-pill btn-pill--success btn-pill--block">
              Talk to us
              <ArrowRight className="h-[18px] w-[18px]" />
            </Link>
          </div>
        </div>
      </div>
    </section>
  );
};

const UseCases = () => (
  <PageLayout
    title="Use Cases — BPO, Field Sales, Insurance, Real Estate & More"
    description="See how WASLX powers outbound calling teams across industries — BPOs, real estate agencies, insurance telecallers, NGOs, edtech, and D2C field sales. No hardware needed."
    path="/use-cases"
  >
    <UseCasesHero />

    {USE_CASES.map((useCase, i) => (
      <UseCaseSection key={useCase.label} useCase={useCase} index={i} />
    ))}

    <ClosingCta />
  </PageLayout>
);

export default UseCases;
